import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
	GoogleReCaptchaProvider,
	useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { Button, Flex, Text } from 'src/components/ui';
import { FormProvider, useForm } from 'react-hook-form';
import { StringInputHook } from 'src/components/common/form';
import AlertMessage from '../common/AlertMessage';
import authService from 'src/services/auth';
import { isMobile } from '../campaigns/utils/constants';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

interface IAuthForm {
	identifier: string;
}

const AuthFormContent = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [userIdentifier, setUserIdentifier] = useState('');

	const formMethods = useForm<IAuthForm>();
	const fontSize = isMobile() ? '16px' : '14px';
	const [searchParams] = useSearchParams();
	const campaignParam = searchParams.get('campaign');
	const campaignIndex = campaignParam !== null ? Number(campaignParam) : undefined;

	const { executeRecaptcha } = useGoogleReCaptcha();

	const handleIdentifierSubmit = formMethods.handleSubmit(async (formData) => {
		setError(null);
		setSuccess(null);

		if (!executeRecaptcha) {
			setError('reCAPTCHA not ready. Please try again.');
			return;
		}

		setIsLoading(true);

		try {
			// Execute reCAPTCHA v3 with 'auth' action
			const captchaToken = await executeRecaptcha('auth');

			if (!captchaToken) {
				setError('Failed to verify captcha. Please try again.');
				setIsLoading(false);
				return;
			}

			const result = await authService.initiateAuth(formData.identifier, undefined, captchaToken, campaignIndex);

			setUserIdentifier(formData.identifier);

			if (result.requiresEmailVerification) {
				// Show success message for email verification
				setSuccess(result.message);
				setIsLoading(false);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.');
		}
	});

	const handleBackToStart = () => {
		setError(null);
		setSuccess(null);
		setUserIdentifier('');
		formMethods.reset();
	};

	// Email Verification Success View
	if (success) {
		return (
			<Flex flex={1} direction="column" gap={6}>
				<AlertMessage
					status="success"
					bg="#E8F5E9"
					borderColor="#4CAF50"
					borderWidth="1px"
				>
					{success}
				</AlertMessage>

				<Flex direction="column" gap={2}>
					<Text fontSize="lg" fontWeight="600" color="gray.700">
						We sent a verification link to:
					</Text>
					<Text fontSize="xl" fontWeight="bold" color="gray.900">
						{userIdentifier}
					</Text>

					<Text fontSize="sm" color="gray.600" mt={4}>
						Check your inbox and click the link to continue.
					</Text>
				</Flex>

				<Flex direction="column" gap={2} mt={2}>
					<Text fontSize="sm" color="gray.500">
						Didn't receive the email? Check your spam folder or try again.
					</Text>

					<Button
						variant="link"
						color="secondary"
						onClick={handleBackToStart}
						mt={2}
						fontWeight="600"
						_hover={{ textDecoration: 'underline' }}
					>
						Use a different email
					</Button>
				</Flex>
			</Flex>
		);
	}

	// Initial Input View - Email Only
	return (
		<Flex flex={1} direction="column" gap={3}>
			<FormProvider {...formMethods}>
				{error && <AlertMessage status="error">{error}</AlertMessage>}

				<form onSubmit={handleIdentifierSubmit} style={{ width: '100%' }}>
					<Flex direction="column" gap={5}>
						<Flex direction="column" gap={2}>
							<Text fontSize="2xl" fontWeight="bold" mb={2}>
								Welcome
							</Text>
							<Text fontSize="sm" color="gray.600">
								Enter your email address to sign in or create an account.
							</Text>
						</Flex>

						<StringInputHook
							label="Email Address"
							name="identifier"
							type="email"
							placeholder="you@example.com"
							required
							inputProps={{
								size: 'md',
								fontSize: fontSize,
								autoComplete: 'email',
								autoFocus: true,
							}}
						/>

						<Flex direction="column" gap={1} mt={-3}>
							<Text fontSize="xs" color="gray.text">
								We'll send you a verification link via email
							</Text>
						</Flex>

						<Button
							variant="orangeSolid"
							type="submit"
							loadingText="Processing..."
							isLoading={isLoading}
							width="100%"
							size="lg"
						>
							Continue
						</Button>

						<Flex direction="column" gap={2} mt={4}>
							<Text fontSize="xs" color="gray.500" textAlign="center">
								By continuing, you agree to our{' '}
								<Link to="https://www.fusionos.ai/terms-of-service" target="_blank">
									<Text as="span" textDecoration="underline" color="secondary">
										Terms of Service
									</Text>
								</Link>{' '}
								and{' '}
								<Link to="https://www.fusionos.ai/privacy-policy" target="_blank">
									<Text as="span" textDecoration="underline" color="secondary">
										Privacy Policy
									</Text>
								</Link>
							</Text>
						</Flex>
					</Flex>
				</form>
			</FormProvider>
		</Flex>
	);
};

// Wrap with GoogleReCaptchaProvider
const AuthForm = () => (
	<GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
		<AuthFormContent />
	</GoogleReCaptchaProvider>
);

export default AuthForm;
